import type { Ref } from "vue";
import { onActivated, onDeactivated, onMounted, ref, watch } from "vue";

import { boardActivated, boardDeactivated } from "@/analytics/events/board";
import { trackEvent } from "@/analytics/track";
import { loadBoard } from "@/backend/read/BackendReader";
import { useEventBus } from "@/composables/useEventBus";
import type { Board } from "@/model/board";
import { isFinishedState } from "@/model/board";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useSessionStore } from "@/store/session";
import { NO_TEAM } from "@/store/team";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";

export function useBaseBoard(board: Ref<Board>, applyBoardRect: () => void) {
  const active = ref(false);
  const isLoading = ref(false);
  const eventBus = useEventBus();

  onMounted(() => {
    // When the WS reconnects, fetch any changes that might have happened while diconnected
    eventBus.onReconnect(() => {
      if (active.value && board.value.state.state !== "inited") {
        void loadBoard(board.value, { subscribe: false });
      }
    });

    setupLoadingWatcher();
    setupWorkingModeWatcher();
    initWorkingMode();
  });

  /**
   * Initialize the working mode from persisted client settings.
   * If this is the first login after iteration start, default to execution mode.
   */
  function initWorkingMode() {
    const lastLoginAt = parseInt(localStorage.getItem("lastLoginAt") || "0");
    const iterStart = useSessionStore().iterations.at(0)?.start.getTime();
    const isFirstLoginAfterIterationStart =
      iterStart && lastLoginAt && Date.now() > iterStart && lastLoginAt < iterStart;

    if (isFirstLoginAfterIterationStart) {
      useWorkModeStore().setWorkingMode("execution");
      return;
    }

    const workingMode = useClientSettingsStore().workingMode;
    useWorkModeStore().setWorkingMode(workingMode);
  }

  // set isLoading to true with some delay to avoid quickly blinking of the loading indicator
  function setupLoadingWatcher() {
    watch(
      () => board.value.state.state,
      () => {
        if (isFinishedState(board.value.state)) {
          isLoading.value = false;
        } else {
          setTimeout(() => (isLoading.value = !isFinishedState(board.value.state)), 100);
        }
      },
    );
  }

  /**
   * When switching to execution mode, reload the board to fetch sticky status distribution if needed
   */
  function setupWorkingModeWatcher() {
    watch(
      () => useWorkModeStore().isExecutionMode,
      (isExecutionMode) => {
        if (isExecutionMode && useBoardStore().currentBoard().id === board.value.id) {
          if (board.value.state.state === "inited") {
            // board not loaded yet, enriched stickies will be loaded with the board
            return;
          }

          const shouldLoadDistribution = !board.value.state.hasStickyStatusDistribution;
          if (shouldLoadDistribution) {
            // Reload the board's stickies, with enriched status distributions
            board.value.state.loadedAt = 0; // Force all stickies to be fetched
            void loadBoard(board.value, {
              subscribe: false,
              skipBoardData: true, // Don't need to refetch objectives
              skipIterations: true, // Enriched iterations are already fetched in planning mode
            });
          }
        }
      },
    );
  }

  onActivated(() => {
    active.value = true;
    applyBoardRect();
    trackBoardEvent(boardActivated);
  });

  onDeactivated(() => {
    active.value = false;
    trackBoardEvent(boardDeactivated);
  });

  /**
   * Tracks the given board event (boardActivated or boardDeactivated)
   */
  function trackBoardEvent(boardEvent: typeof boardActivated | typeof boardDeactivated) {
    const userTeams = useUserStore().technicalUser.teams;
    const teamId = "team" in board.value ? board.value.team?.id : undefined;

    const matchesUsersTeam = userTeams.some((team) => team.id === teamId);
    const matchesUsersArt = userTeams.some((team) => team.artId === board.value.artId);

    // when no session is found, a dummy NO_TEAM board is activated, don't track this
    if (teamId !== NO_TEAM.id) {
      trackEvent(
        boardEvent({
          type: board.value.type,
          id: board.value.id,
          artId: board.value.artId,
          teamId,
          matchesUsersArt,
          matchesUsersTeam,
        }),
      );
    }
  }

  return { active, isLoading };
}
